import type { BillPayment } from '~/types/payVendorBills';

export interface BillFundingStatus {
  funded: boolean;
  runningTotal: number;
}

export interface PropertyFundingSummary {
  hasPool: boolean;
  availableForPayment: number;
  totalToPay: number;
  shortfall: number;
  unfundedCount: number;
  billStatuses: Map<string, BillFundingStatus>;
}

const emptySummary = (availableForPayment: number, hasPool: boolean): PropertyFundingSummary => ({
  hasPool,
  availableForPayment,
  totalToPay: 0,
  shortfall: 0,
  unfundedCount: 0,
  billStatuses: new Map(),
});

export const sortBillsByFundingPriority = (bills: BillPayment[]): BillPayment[] =>
  [...bills].sort((left, right) => {
    if (left.dueDate === right.dueDate) return left.id.localeCompare(right.id);
    return left.dueDate < right.dueDate ? -1 : 1;
  });

export const groupPropertyBillsByVendor = (bills: BillPayment[]): BillPayment[][] => {
  const map = new Map<string | undefined, BillPayment[]>();
  for (const bill of sortBillsByFundingPriority(bills)) {
    const existing = map.get(bill.vendor?.id);
    if (existing) existing.push(bill);
    else map.set(bill.vendor?.id, [bill]);
  }
  return Array.from(map.values());
};

const orderPropertyBillsForFunding = (bills: BillPayment[]): BillPayment[] => groupPropertyBillsByVendor(bills).flat();

export const computePropertyFunding = (bills: BillPayment[]): PropertyFundingSummary => {
  const poolBill = bills.find((bill) => bill.property?.availableForPayment != null);
  const availableForPayment = poolBill?.property?.availableForPayment;
  const hasPool = availableForPayment != null;

  if (!hasPool) return emptySummary(0, false);

  const summary = emptySummary(availableForPayment, true);
  let runningTotal = 0;

  for (const bill of orderPropertyBillsForFunding(bills)) {
    if (bill.excluded) {
      summary.billStatuses.set(bill.id, { funded: true, runningTotal });
      continue;
    }

    runningTotal += bill.paymentAmount ?? 0;
    const funded = runningTotal <= availableForPayment;
    summary.billStatuses.set(bill.id, { funded, runningTotal });

    if (!funded) summary.unfundedCount += 1;
  }

  summary.totalToPay = runningTotal;
  summary.shortfall = Math.max(0, runningTotal - availableForPayment);

  return summary;
};

export const computeFundingByProperty = (billsByProperty: BillPayment[][]): Map<string | undefined, PropertyFundingSummary> => {
  const map = new Map<string | undefined, PropertyFundingSummary>();
  for (const bills of billsByProperty) {
    map.set(bills.at(0)?.property?.id, computePropertyFunding(bills));
  }
  return map;
};
